import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import { EAT_CUISINES } from '../common/eatCatalog';
import { api } from 'common';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

/* ─── CSS keyframes injectées une seule fois ─────────────────────────── */
const STYLE_ID = 'eat-slider-keyframes';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes eatFadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes eatBadgePulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(234,179,8,0.5); }
      50%       { box-shadow: 0 0 0 8px rgba(234,179,8,0); }
    }
    @keyframes eatShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes eatCardReveal {
      from { opacity: 0; transform: scale(0.94) translateY(20px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes eatDotBlink {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.3; }
    }
  `;
  document.head.appendChild(style);
}

const GOLD = colors.Button_Primary;
const GOLD_DIM = 'rgba(212,175,55,0.15)';
const GOLD_BORDER = 'rgba(212,175,55,0.25)';

const getCuisineEmoji = (cuisineId) => {
  const found = EAT_CUISINES.find(c => c.id === cuisineId);
  return found ? found.emoji : '🍽️';
};

const getCuisineLabel = (cuisineId) => {
  const found = EAT_CUISINES.find(c => c.id === cuisineId);
  return found ? found.label : cuisineId || '';
};

/* ─── Shimmer placeholder ─────────────────────────────────────────────── */
const SkeletonCard = () => (
  <Box sx={{
    width: { xs: '85vw', sm: 280 },
    minWidth: { xs: 240, sm: 280 },
    maxWidth: 280,
    height: 360,
    borderRadius: '20px',
    background: 'linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%)',
    backgroundSize: '600px 100%',
    animation: 'eatShimmer 1.5s infinite linear',
    border: `1px solid ${GOLD_BORDER}`,
    flexShrink: 0,
    scrollSnapAlign: 'start'
  }} />
);

/* ─── Card individuelle ───────────────────────────────────────────────── */
const RestaurantCard = React.memo(({ restaurant, index, visible }) => {
  const city = restaurant.city || restaurant.address?.split(',').pop()?.trim() || '';
  const cuisine = getCuisineEmoji(restaurant.cuisine);
  const cuisineLabel = getCuisineLabel(restaurant.cuisine);
  const isOpen = restaurant.isOpen !== false;

  return (
    <Box
      sx={{
        width: { xs: '85vw', sm: 280 },
        minWidth: { xs: 240, sm: 280 },
        maxWidth: 280,
        borderRadius: '20px',
        overflow: 'visible',
        background: 'linear-gradient(160deg, #1c1c1c 0%, #0d0d0d 100%)',
        border: `1px solid ${GOLD_BORDER}`,
        flexShrink: 0,
        cursor: 'default',
        position: 'relative',
        zIndex: 1,
        scrollSnapAlign: 'start',
        transition: 'transform 0.35s cubic-bezier(.22,.68,0,1.2), border-color 0.3s, box-shadow 0.35s',
        opacity: visible ? 1 : 0,
        animation: visible ? `eatCardReveal 0.55s cubic-bezier(.22,.68,0,1.2) ${index * 80}ms both` : 'none',
        clipPath: 'inset(0 0 0 0 round 20px)',
        '&:hover': {
          zIndex: 4,
          transform: 'translateY(-10px) scale(1.02)',
          borderColor: GOLD,
          boxShadow: `0 20px 60px rgba(212,175,55,0.2), 0 4px 16px rgba(0,0,0,0.6)`
        }
      }}
    >
      {/* Cover image */}
      <Box sx={{
        width: '100%', height: 180, position: 'relative',
        backgroundColor: GOLD_DIM,
        overflow: 'hidden'
      }}>
        {restaurant.coverImageUrl ? (
          <img
            src={restaurant.coverImageUrl}
            alt={restaurant.name}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transition: 'transform 0.5s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        ) : (
          <Box sx={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${GOLD_DIM}, rgba(0,0,0,0.4))`
          }}>
            <RestaurantIcon sx={{ fontSize: 64, color: GOLD, opacity: 0.4 }} />
          </Box>
        )}

        {/* Dégradé en bas de l'image */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 80,
          background: 'linear-gradient(to top, #0d0d0d 0%, transparent 100%)'
        }} />

        {/* Statut ouvert/fermé */}
        <Box sx={{
          position: 'absolute', top: 10, right: 10,
          display: 'flex', alignItems: 'center', gap: 0.5,
          backgroundColor: isOpen ? 'rgba(212,175,55,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${isOpen ? 'rgba(212,175,55,0.4)' : 'rgba(239,68,68,0.4)'}`,
          borderRadius: '20px', px: 1.2, py: 0.3
        }}>
          <FiberManualRecordIcon sx={{
            fontSize: 8,
            color: isOpen ? '#d4af37' : '#ef4444',
            animation: isOpen ? 'eatDotBlink 2s infinite' : 'none'
          }} />
          <Typography sx={{
            fontFamily: FONT_FAMILY, fontSize: '11px', fontWeight: 700,
            color: isOpen ? '#d4af37' : '#ef4444', letterSpacing: '0.04em'
          }}>
            {isOpen ? 'Ouvert' : 'Fermé'}
          </Typography>
        </Box>

        {/* Logo restaurant en overlay — positionné dans l'image, au-dessus du dégradé */}
        {restaurant.logoUrl && (
          <Box sx={{
            position: 'absolute', bottom: 10, left: 14,
            width: 42, height: 42, borderRadius: '10px',
            overflow: 'hidden', border: `2px solid ${GOLD}`,
            backgroundColor: '#000', flexShrink: 0,
            zIndex: 2
          }}>
            <img src={restaurant.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        )}
      </Box>

      {/* Contenu */}
        <Box sx={{ px: 2, pt: 2, pb: 2 }}>
        {/* Nom */}
        <Typography sx={{
          fontFamily: FONT_FAMILY, fontSize: '17px', fontWeight: 700,
          color: '#fff', mb: 0.5,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {restaurant.name}
        </Typography>

        {/* Cuisine + ville */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
            {cuisine} {cuisineLabel}
          </Typography>
          {city && (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: GOLD_BORDER }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <LocationOnIcon sx={{ fontSize: 13, color: GOLD, opacity: 0.7 }} />
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>
                  {city}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Note */}
        {restaurant.rating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <StarRoundedIcon sx={{ fontSize: 15, color: GOLD }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '13px', color: GOLD, fontWeight: 700 }}>
              {restaurant.rating}
            </Typography>
            {restaurant.ratingCount > 0 && (
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                ({restaurant.ratingCount} avis)
              </Typography>
            )}
          </Box>
        )}

        {/* Badge Allô Driver Eats */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.7,
          background: 'linear-gradient(90deg, rgba(234,179,8,0.12) 0%, rgba(212,175,55,0.18) 100%)',
          border: `1px solid ${GOLD}`,
          borderRadius: '20px', px: 1.5, py: 0.5,
          flexWrap: 'wrap',
          animation: 'eatBadgePulse 3s ease-in-out infinite'
        }}>
          <RestaurantIcon sx={{ fontSize: 13, color: GOLD }} />
          <Typography sx={{
            fontFamily: FONT_FAMILY, fontSize: '11px', fontWeight: 800,
            color: GOLD, letterSpacing: '0.06em', textTransform: 'uppercase'
          }}>
            Disponible sur Allô Driver Eats !
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

/* ─── Composant principal ─────────────────────────────────────────────── */
const EatRestaurantsSlider = React.memo(() => {
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [fetched, setFetched] = useState(false);

  const restaurants = useSelector(state => state.eatdata?.restaurants || []);

  const langCode = useMemo(() =>
    i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr',
    [i18n?.language]
  );

  // Fetch restaurants si pas encore chargé
  useEffect(() => {
    if (!fetched) {
      dispatch(api.fetchEatRestaurants());
      setFetched(true);
    }
  }, [dispatch, fetched]);

  // IntersectionObserver pour déclencher l'animation à l'entrée dans le viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const displayedRestaurants = useMemo(() => {
    const active = restaurants.filter(r =>
      !r.archived && !r.deletedAt && r.active !== false
    );
    // priorité à ceux qui ont une image de couverture
    const withCover = active.filter(r => r.coverImageUrl);
    const withoutCover = active.filter(r => !r.coverImageUrl);
    return [...withCover, ...withoutCover].slice(0, 20);
  }, [restaurants]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += dir === 'left' ? -320 : 320;
    }
  };

  const content = langCode === 'fr' ? {
    title: 'Nos restaurants partenaires',
    subtitle: 'Commandez vos plats préférés livrés chez vous',
    cta: 'Voir tous les établissements'
  } : {
    title: 'Our partner restaurants',
    subtitle: 'Order your favourite dishes delivered to your door',
    cta: 'View all restaurants'
  };

  if (!displayedRestaurants.length && !fetched) return null;

  return (
    <Box
      ref={sectionRef}
      id="section-eats"
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 0, md: 0 },
        backgroundColor: '#000',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Halo décoratif */}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 900, height: 400, borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(212,175,55,0.04) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      <Container maxWidth="lg">
        {/* Titre */}
        <Box sx={{
          textAlign: 'center', mb: 6,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(28px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease'
        }}>
          {/* Pill label */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
            borderRadius: '30px', px: 2, py: 0.6, mb: 2
          }}>
            <RestaurantIcon sx={{ fontSize: 16, color: GOLD }} />
            <Typography sx={{
              fontFamily: FONT_FAMILY, fontSize: '12px', fontWeight: 700,
              color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase'
            }}>
              Allô Driver Eats
            </Typography>
          </Box>

          <Typography variant="h2" sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '26px', md: '40px' },
            fontWeight: 800, color: '#fff', lineHeight: 1.15, mb: 1.5
          }}>
            {content.title}
          </Typography>
          <Typography sx={{
            fontFamily: FONT_FAMILY,
            fontSize: { xs: '15px', md: '18px' },
            color: 'rgba(255,255,255,0.6)', maxWidth: 520, mx: 'auto', lineHeight: 1.6
          }}>
            {content.subtitle}
          </Typography>
        </Box>

        {/* Slider */}
        <Box sx={{ position: 'relative' }}>
          {/* Flèche gauche */}
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute', left: { xs: 4, md: -28 },
              top: '50%', transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(212,175,55,0.1)', color: GOLD,
              width: 48, height: 48,
              border: `1px solid ${GOLD_BORDER}`,
              transition: 'all 0.25s',
              '&:hover': { backgroundColor: 'rgba(212,175,55,0.22)', border: `1px solid ${GOLD}` },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          {/* Flèche droite */}
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute', right: { xs: 4, md: -28 },
              top: '50%', transform: 'translateY(-50%)',
              zIndex: 3,
              backgroundColor: 'rgba(212,175,55,0.1)', color: GOLD,
              width: 48, height: 48,
              border: `1px solid ${GOLD_BORDER}`,
              transition: 'all 0.25s',
              '&:hover': { backgroundColor: 'rgba(212,175,55,0.22)', border: `1px solid ${GOLD}` },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>

          {/* Masques latéraux — seulement visibles sur desktop où les flèches existent */}
          <Box sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: { xs: 0, md: 40 }, zIndex: 2,
            background: 'linear-gradient(to right, #000 0%, transparent 100%)',
            pointerEvents: 'none'
          }} />
          <Box sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: { xs: 0, md: 40 }, zIndex: 2,
            background: 'linear-gradient(to left, #000 0%, transparent 100%)',
            pointerEvents: 'none'
          }} />

          {/* Défilement */}
          <Box
            ref={scrollRef}
            sx={{
              display: 'flex', gap: { xs: 2, md: 3 }, overflowX: 'auto',
              scrollBehavior: 'smooth', pb: 2, px: { xs: 2, md: 1 },
              scrollSnapType: { xs: 'x mandatory', md: 'none' },
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { height: '6px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: GOLD, borderRadius: '4px',
                '&:hover': { backgroundColor: '#c4a137' }
              }
            }}
          >
            {displayedRestaurants.length === 0
              ? Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
              : displayedRestaurants.map((r, idx) => (
                <RestaurantCard
                  key={r.id || r.restaurantId || idx}
                  restaurant={r}
                  index={idx}
                  visible={visible}
                />
              ))
            }
          </Box>
        </Box>

        {/* Compteur */}
        {displayedRestaurants.length > 0 && (
          <Box sx={{
            textAlign: 'center', mt: 4,
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.4s'
          }}>
            <Typography sx={{
              fontFamily: FONT_FAMILY, fontSize: '13px',
              color: 'rgba(255,255,255,0.4)',
              '& span': { color: GOLD, fontWeight: 700 }
            }}>
              <span>{displayedRestaurants.length}</span> établissement{displayedRestaurants.length > 1 ? 's' : ''} partenaire{displayedRestaurants.length > 1 ? 's' : ''} sur Allô Driver Eats
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
});

export default EatRestaurantsSlider;
